// Phase 29.1 — Admin panel: Device change requests review.
import { Fragment, useCallback, useEffect, useState } from "react";
import { RefreshCw, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");

// What an admin needs before approving: this user's past devices, whether the
// new device has been used by anyone else, and their earlier requests.
function RequestDetails({ i }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs" data-testid={`dcr-details-${i.id}`}>
      <div>
        <div className="font-semibold text-neutral-700 mb-1">
          {i.user?.name || "User"}'s devices
        </div>
        <div className="text-neutral-500 mb-1">
          Last login {fmt(i.user?.last_login_at)}{i.user?.last_login_ip ? ` · ${i.user.last_login_ip}` : ""}
          {i.bound_since ? ` · bound since ${fmt(i.bound_since)}` : ""}
        </div>
        {(i.user_devices || []).length === 0 ? (
          <div className="text-neutral-400">No device history recorded yet.</div>
        ) : (i.user_devices || []).map((d) => (
          <div key={d.fp} className="py-0.5">
            <span className="font-medium">{d.label || "Unknown device"}</span>
            {d.is_current && <Badge className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">current</Badge>}
            <div className="text-neutral-500">
              {fmt(d.first_seen_at)} → {fmt(d.last_seen_at)} · {d.login_count} login{d.login_count === 1 ? "" : "s"}
              {d.blocked_count ? ` (${d.blocked_count} blocked)` : ""}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="font-semibold text-neutral-700 mb-1">Others on the new device</div>
        {(i.shared_with || []).length === 0 ? (
          <div className="text-neutral-400">No one else has used this device.</div>
        ) : (i.shared_with || []).map((o, idx) => (
          <div key={idx} className="py-0.5">
            <span className="font-medium text-amber-700">{o.name}</span>
            {o.role ? ` (${o.role})` : ""}
            <div className="text-neutral-500">
              {o.currently_bound ? "currently logs in here" : "used it before"}
              {o.last_seen_at ? ` · last ${fmt(o.last_seen_at)}` : ""}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="font-semibold text-neutral-700 mb-1">Earlier requests</div>
        {(i.past_requests || []).length === 0 ? (
          <div className="text-neutral-400">None — first change request.</div>
        ) : (i.past_requests || []).map((r, idx) => (
          <div key={idx} className="py-0.5">
            <span className="font-medium capitalize">{r.status}</span> · {r.new_label || "device"}
            <div className="text-neutral-500">
              {fmt(r.requesting_ts)}{r.decided_by ? ` · by ${r.decided_by}` : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DeviceChangeRequests() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      // `_t` busts any intermediary/browser cache so a decided request never lingers.
      const r = await api.get("/device-change-requests", { params: { status: "pending", _t: Date.now() } });
      setItems((r.data || r).items || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try {
      await api.post(`/device-change-requests/${id}/${action}`);
      toast.success(`Request ${action}d.`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      load();
    } catch (e) { toast.error(`Failed to ${action}: ${e?.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="device-change-requests-page">
      <PageHeader
        title="Device change requests"
        subtitle="A user who logs in on a different phone/browser is blocked until you approve it here. Approving moves their login to the new device."
        actions={<Button variant="outline" onClick={load} data-testid="refresh-dcr"><RefreshCw className="h-4 w-4 mr-2" />Reload</Button>}
      />
      <div className="rounded-2xl bg-white border border-neutral-200 shadow-sm p-5">
        {items.length === 0 ? (
          <div className="text-center py-16 text-neutral-500 text-sm" data-testid="dcr-empty">
            {busy ? "Loading…" : "No pending device change requests."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Old device</TableHead>
                <TableHead>New device</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>IP</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <Fragment key={i.id}>
                <TableRow data-testid={`dcr-row-${i.id}`} className="border-b-0">
                  <TableCell className="text-sm">{i.user?.name || i.user?.email || i.user?.phone || i.user_id}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[11px]">{i.user?.role_code || "—"}</Badge></TableCell>
                  <TableCell className="text-sm">
                    {i.old_label || "—"}
                    <div className="text-[10px] font-mono text-neutral-400" title={i.old_fp}>{(i.old_fp || "").slice(0, 8)}</div>
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {i.new_label || "—"}
                    <div className="text-[10px] font-mono text-neutral-400" title={i.new_fp}>{(i.new_fp || "").slice(0, 8)}</div>
                  </TableCell>
                  <TableCell className="text-xs text-neutral-500">{i.requesting_ts}</TableCell>
                  <TableCell className="text-xs text-neutral-500">{i.ip || "—"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => decide(i.id, "approve")} data-testid={`approve-${i.id}`}>
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => decide(i.id, "reject")} data-testid={`reject-${i.id}`}>
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow className="bg-neutral-50/60">
                  <TableCell colSpan={7} className="pt-1 pb-4"><RequestDetails i={i} /></TableCell>
                </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
